import { Box, Button, Card, Stack, Tooltip, Typography } from "@mui/material";
import { forwardRef, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { adwDownloadQueryDictionary, selectAdvancedFields } from "../../dataWranglerSlice";
import ADWInstrument from "./ADWInstrument";
import { ViewportList } from "react-viewport-list";
import SignalCellularAltIcon from '@mui/icons-material/SignalCellularAlt';
import { colours } from "../../../../StyleConstants";
import { useUserInfoQuery } from "../../../../api/userApiSlice";
import SaveOutlinedIcon from "@mui/icons-material/SaveOutlined";

interface Props {
    setTooManySelected: (arg0: boolean) => void,
    setOpenSaveQueryModal: (arg: boolean) => void,
    setOpenRunQueryModal: (arg: boolean) => void,
}

const ADWQuery = forwardRef<HTMLDivElement, Props>(function ADWQuery(props, ref) {
    const { data: userData } = useUserInfoQuery({});
    const divRef = useRef(null);
    const dispatch = useDispatch();
    const fields = useSelector(selectAdvancedFields);
    const maxSelected = 16000;
    const role = userData ? userData.roles : ["SUMMARY"];
    const userIsAdmin = role && role.includes("ADMIN");
    const userHasSubjectLevel = role.includes("SUBJECT");
    const userOnlyHasSummaryLevel = window?.env?.onlyDuc && !userIsAdmin && !userHasSubjectLevel;

    const data = Object.keys(fields || {}).map((inst) => {
        return {
            name: inst,
            fields: fields[inst]
        };
    });

    const fieldsHasItems = Object.keys(fields).length > 0;

    const runQuery = () => {
        const numSelected = data.map((current) => Object.keys(current.fields.fields || {}).length).reduce((acc, curr) => acc + curr);
        if (numSelected >= maxSelected) {
            props.setTooManySelected(true)
        } else {
            props.setOpenRunQueryModal(true);
        }
    }

    const downloadQueryDictionary = () => {
        const numSelected = data.map((current) => Object.keys(current.fields.fields || {}).length).reduce((acc, curr) => acc + curr);
        if (numSelected >= maxSelected) {
            props.setTooManySelected(true)
        } else {
            dispatch(adwDownloadQueryDictionary())
        }
    }

    const queryTabularButtonTooltipTitle =
        userOnlyHasSummaryLevel ?
            "This button is temporarily disabled, when full data download is available it will be open for users who have a valid DUC and have passed training" :
            !userHasSubjectLevel && !userIsAdmin ?
                "Complete Data Use Certification (DUC) to access all features" :
                "Please ensure you have selected at least an instrument before running a query";

    const queryDisabled =
        (Object.keys(fields || {}).length === 0) ||
        (!userIsAdmin && (window?.env?.onlyDuc || !userHasSubjectLevel))

    const queryDictionaryDisabled = (Object.keys(fields || {}).length === 0);

    return (
        <Card
            sx={{
                flexGrow: 1,
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'spaceBetween',
                gap: 1,
                padding: 3,
                height: '100%',
                borderRadius: '4px',
                margin: 0
            }}
            ref={ref}
        >
            <Typography variant={'h5'} fontWeight={'bold'}>
                Selected Tabulated Data
            </Typography>
            <div ref={divRef} style={{
                overflow: 'auto',
                padding: "8px 0px",
                background: fieldsHasItems ? 'transparent' : colours.midnightBlue.blue5,
                borderRadius: '4px',
                border: fieldsHasItems ? 'unset' : `2px dashed ${colours.gray.gray80}`,
                flexGrow: 1
            }}>
                {!fieldsHasItems ?
                    <>
                        <Box sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            gap: 4,
                            flexGrow: 1,
                            height: '100%',
                            alignItems: 'center',
                            justifyContent: 'center',
                        }}>
                            <Typography align="center" variant="h6" sx={{ padding: 3 }} fontWeight="bold">
                                Drag fields to this group to download selected tabulated data
                            </Typography>
                        </Box>
                        { /* Need to define  ViewportList twice to fix a scroll issue when query is reset */}
                        <ViewportList
                            viewportRef={divRef}
                            items={data}
                        >
                            {(item, index) => (
                                <ADWInstrument
                                    key={`inst_${index}`}
                                    originalData={item}
                                    name={item.name}
                                    instrument={item.fields}
                                    editable={true}
                                />
                            )}
                        </ViewportList>
                    </> :
                    <ViewportList
                        viewportRef={divRef}
                        items={data}
                    >
                        {(item, index) => (
                            <ADWInstrument
                                key={`inst_${index}`}
                                originalData={item}
                                name={item.name}
                                instrument={item.fields}
                                editable={true}
                            />
                        )}
                    </ViewportList>
                }
            </div>
            <Stack
                flexDirection="row"
                gap={2}
                sx={{ marginTop: "auto", width: "100%" }}
            >
                <Button
                    variant="secondary"
                    disabled={Object.keys(fields || {}).length === 0}
                    startIcon={<SaveOutlinedIcon/>}
                    onClick={() => props.setOpenSaveQueryModal(true)}
                    aria-haspopup={true}
                >
                    Save Query
                </Button>

                <Tooltip title={queryDisabled ? queryTabularButtonTooltipTitle : ""}>
                    <span style={{ width: "100%" }}>
                        <Button
                            variant="primary"
                            startIcon={<SignalCellularAltIcon/>}
                            disabled={queryDisabled}
                            onClick={runQuery}
                            aria-haspopup={true}
                            sx={{ width: "100%", height: "100%" }}
                        >
                            Download Data
                        </Button>
                    </span>
                </Tooltip>
                <Button
                    variant="primary"
                    startIcon={<SignalCellularAltIcon/>}
                    disabled={queryDictionaryDisabled}
                    onClick={downloadQueryDictionary}
                    aria-haspopup={true}
                >
                    Download Data Dictionary
                </Button>
            </Stack>
            <Typography sx={{ textTransform: 'uppercase' }} align="center" variant="caption">
                Exports data as a single CSV file.
            </Typography>
        </Card>
    )
})

export default ADWQuery;
